/**
 * Trailblazers (TRL) triangulation types.
 *
 * A triangulation planet holds exactly 3 screenshots from the same location.
 * Locating all 3 is ALWAYS a parallel objective — the server matches each dig to
 * the closest not-yet-found image, so a team can split up and collect the points
 * in any order, concurrently (see `handleTriangulationDig`).
 *
 * What this module adds is the optional SECOND phase. With
 * {@link TrlTriangulationConfig.simultaneousBroadcast} on, finding the 3 points
 * no longer reveals the secret 4th bury coordinate. Instead the team must return
 * to the points and re-scan them TOGETHER: the server has to see 3 successful
 * digs — on 3 distinct points, by 3 distinct players — inside one rolling window
 * ({@link TrlTriangulationConfig.broadcastWindowSeconds}). That is why the mode
 * implies a hard {@link TRL_SIMULTANEOUS_MIN_TEAM_SIZE} floor on team size.
 *
 * The config lives game-level on `games.trl_triangulation_config`. Unlike the
 * stalemate/radar configs there is deliberately NO per-node override — the rule
 * is a whole-game format choice, and a mixed map would make the min-team-size
 * guarantee impossible to reason about.
 */
// ============================================================================
// Bounds
// ============================================================================
/** The broadcast window is an integer number of seconds in [5, 60]. */
export const TRL_BROADCAST_WINDOW_MIN_SECONDS = 5;
export const TRL_BROADCAST_WINDOW_MAX_SECONDS = 60;
/**
 * Simultaneous broadcast needs one player per triangulation point, so a team
 * below this size can never complete a triangulation node. Enforced at game
 * start (`preflightTrlStart`) and surfaced as a warning in the admin Teams tab.
 */
export const TRL_SIMULTANEOUS_MIN_TEAM_SIZE = 3;
/**
 * Nominal length of the seismic tool's "Component Scan" cast. The server only
 * scores a dig when the cast completes, so a cast that has STARTED is projected
 * to land this long after its start when computing the team's live x/3 count.
 */
export const TRL_SEISMIC_CAST_MS = 16000;
/** Defaults: the classic (async) behaviour, so existing games are unaffected. */
export const DEFAULT_TRL_TRIANGULATION_CONFIG = {
    simultaneousBroadcast: false,
    broadcastWindowSeconds: 15,
};
/**
 * Resolve the effective config for a game. There is no per-node override, so
 * this is simply "authored config, else built-in defaults" — kept as a function
 * for symmetry with {@link resolveTrlRadarConfig} and so every call site reads
 * the same way.
 */
export function resolveTrlTriangulationConfig(gameConfig) {
    return gameConfig ?? DEFAULT_TRL_TRIANGULATION_CONFIG;
}
/** Value-equality across every persisted field. */
export function trlTriangulationConfigEquals(a, b) {
    return (a.simultaneousBroadcast === b.simultaneousBroadcast &&
        a.broadcastWindowSeconds === b.broadcastWindowSeconds);
}
/**
 * Sanitise untrusted admin input into a valid {@link TrlTriangulationConfig}:
 * coerce the toggle to a boolean and clamp the window to an integer in
 * [5, 60] seconds, falling back per-field to the defaults. Mirrors
 * `normalizeTrlRadarConfig` — normalise, never reject.
 */
export function normalizeTrlTriangulationConfig(raw) {
    if (!raw || typeof raw !== 'object')
        return { ...DEFAULT_TRL_TRIANGULATION_CONFIG };
    const it = raw;
    const secondsRaw = Number(it.broadcastWindowSeconds);
    const broadcastWindowSeconds = Number.isFinite(secondsRaw)
        ? Math.min(TRL_BROADCAST_WINDOW_MAX_SECONDS, Math.max(TRL_BROADCAST_WINDOW_MIN_SECONDS, Math.round(secondsRaw)))
        : DEFAULT_TRL_TRIANGULATION_CONFIG.broadcastWindowSeconds;
    return {
        simultaneousBroadcast: Boolean(it.simultaneousBroadcast),
        broadcastWindowSeconds,
    };
}
/**
 * Drop ledger entries older than the window relative to `nowMs`, and collapse
 * repeats so at most one entry survives per (point, player) pair — the newest.
 * Keeps the persisted ledger bounded (≤ 9 entries for a 3-point node) so
 * {@link resolveBroadcastCompletion} can brute-force safely.
 */
export function pruneBroadcastDigs(entries, windowMs, nowMs) {
    const newest = new Map();
    for (const e of entries) {
        if (nowMs - e.atMs > windowMs)
            continue;
        const key = `${e.nodeImageId}:${e.playerId}`;
        const prev = newest.get(key);
        if (!prev || e.atMs > prev.atMs)
            newest.set(key, e);
    }
    return [...newest.values()].sort((a, b) => a.atMs - b.atMs);
}
/**
 * Does this ledger contain `required` entries on DISTINCT points, by DISTINCT
 * players, whose timestamps all fall inside `windowMs` of each other?
 *
 * Rolling, not anchored: any qualifying subset completes the broadcast, so a
 * partial attempt simply ages out and the team may retry freely with no penalty.
 * Brute-forces every combination — safe because `pruneBroadcastDigs` bounds the
 * ledger at `required²` entries.
 *
 * `activeCount` is the best partial progress to show the team: the largest
 * distinct-point / distinct-player subset that currently fits in one window.
 */
export function resolveBroadcastCompletion(entries, required, windowMs) {
    if (required <= 0)
        return { complete: true, activeCount: 0 };
    const activeCount = bestBroadcastPoints(entries, required, windowMs).length;
    return activeCount >= required
        ? { complete: true, activeCount: required }
        : { complete: false, activeCount };
}
/**
 * The points behind {@link resolveBroadcastCompletion}'s `activeCount`: the
 * node-image ids of the largest distinct-point / distinct-player subset that
 * fits in one window. Lets the team see WHICH points are currently held, so a
 * player who has drifted off (or doubled up on) a point can tell.
 */
export function resolveBroadcastActivePoints(entries, required, windowMs) {
    if (required <= 0)
        return [];
    return bestBroadcastPoints(entries, required, windowMs);
}
/** Largest qualifying point set across every candidate rolling window. */
function bestBroadcastPoints(entries, required, windowMs) {
    const sorted = [...entries].sort((a, b) => a.atMs - b.atMs);
    let best = [];
    // Each candidate window starts at one entry; entries are sorted, so the window
    // holds every later entry within `windowMs` of it.
    for (let i = 0; i < sorted.length; i++) {
        const windowEntries = sorted.filter((e) => e.atMs >= sorted[i].atMs && e.atMs - sorted[i].atMs <= windowMs);
        const points = largestDistinctSubset(windowEntries, required);
        if (points.length > best.length)
            best = points;
        if (best.length >= required)
            break;
    }
    return best;
}
/**
 * Largest set of entries selectable from `windowEntries` such that no two share
 * a point and no two share a player — a tiny bipartite matching, solved by
 * exhaustive search since the input is at most `required²` entries. Returns the
 * chosen entries' point ids.
 */
function largestDistinctSubset(windowEntries, required) {
    let best = [];
    const search = (index, points, players) => {
        if (points.size > best.length)
            best = [...points];
        if (best.length >= required || index >= windowEntries.length)
            return;
        // Prune: even taking every remaining entry can't beat the current best.
        if (points.size + (windowEntries.length - index) <= best.length)
            return;
        const e = windowEntries[index];
        if (!points.has(e.nodeImageId) && !players.has(e.playerId)) {
            points.add(e.nodeImageId);
            players.add(e.playerId);
            search(index + 1, points, players);
            points.delete(e.nodeImageId);
            players.delete(e.playerId);
        }
        search(index + 1, points, players);
    };
    search(0, new Set(), new Set());
    return best;
}
